/**
 * Detects when a new production build is deployed while the user still has an old
 * JS bundle in memory or from cache, then triggers a full reload so they match the
 * server without Ctrl+F5.
 *
 * Ops note: also serve index.html with Cache-Control: no-cache (or very short max-age)
 * so browsers fetch the latest HTML that points at hashed static files.
 */

function basenameFromPath(p) {
  if (typeof p !== "string") return null;
  const clean = p.split("?")[0];
  const parts = clean.split("/");
  return parts[parts.length - 1] || null;
}

function getServerMainFilename(manifest) {
  const files = manifest?.files;
  const mainPath = files?.["main.js"] || files?.["main.mjs"];
  if (typeof mainPath === "string") return basenameFromPath(mainPath);

  const entrypoints = manifest?.entrypoints;
  if (Array.isArray(entrypoints)) {
    for (const ep of entrypoints) {
      if (typeof ep !== "string") continue;
      if (/\/main\.[^/]+\.(js|mjs)$/.test(ep)) return basenameFromPath(ep);
    }
  }
  return null;
}

function getLoadedMainFilename() {
  const scripts = document.querySelectorAll('script[src*="static/js"]');
  for (const s of scripts) {
    const src = s.getAttribute("src") || "";
    if (/\/main\.[^/]+\.(js|mjs)(\?|$)/.test(src)) {
      return basenameFromPath(src);
    }
  }
  return null;
}

async function runCheckOnce() {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const url = `${publicUrl}/asset-manifest.json?t=${Date.now()}`;
  const res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) return;

  const manifest = await res.json();
  const serverMain = getServerMainFilename(manifest);
  const loadedMain = getLoadedMainFilename();
  if (!serverMain || !loadedMain) return;

  if (serverMain !== loadedMain) {
    window.location.reload();
  }
}

/**
 * @param {{ intervalMs?: number; checkOnFocus?: boolean }} [options]
 * @returns {() => void} cleanup
 */
export function registerDeployUpdateCheck(options = {}) {
  if (process.env.NODE_ENV !== "production") {
    return () => { };
  }

  const intervalMs = options.intervalMs ?? 120_000;
  const checkOnFocus = options.checkOnFocus !== false;

  let intervalId;
  const schedule = () => {
    void runCheckOnce();
  };

  schedule();
  intervalId = setInterval(schedule, intervalMs);

  const onVisibility = () => {
    if (checkOnFocus && document.visibilityState === "visible") {
      schedule();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
